/* Grab and swipe a table that is wider than its box.
 *
 * A touch screen already pans an overflow-x container natively, so this is for
 * a mouse: press anywhere on the table and drag it sideways, the way you would
 * a map. What it replaces is the scrollbar, which sits at the bottom of the
 * table rather than the bottom of the screen — two hundred rows down on
 * Accounts, so reaching it meant scrolling to the end of the register, moving
 * sideways, and scrolling back up to read what you had uncovered.
 *
 * A drag must not also open a record. Rows carry a click handler, so past a few
 * pixels of movement the click that follows is swallowed in the capture phase
 * before it reaches the row. Below that threshold nothing is suppressed and a
 * click is still a click — otherwise a slightly unsteady press would stop
 * opening records, which is a worse fault than the one being fixed.
 */

import { useEffect, useRef, useState } from 'react';

/** Pixels of movement before a press counts as a pan rather than a click. */
const DRAG_THRESHOLD = 4;

export function useDragScroll<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  /** Whether there is anywhere to pan to, so the cursor only offers it if so. */
  const [pannable, setPannable] = useState(false);
  const [panning, setPanning] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const check = () => setPannable(el.scrollWidth > el.clientWidth + 1);
    check();

    /* Both boxes, because either can move independently: the wrapper narrows
       when the record panel opens, and the table narrows when a container query
       drops a column. Watching only one leaves the cursor lying. */
    const ro = new ResizeObserver(check);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let startX = 0;
    let startLeft = 0;
    let active = false;
    let moved = false;
    let suppress = false;

    const down = (e: PointerEvent) => {
      /* Touch and pen already pan this natively and do it better — with
         momentum, and without fighting the page's vertical scroll. */
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      if (el.scrollWidth <= el.clientWidth) return;
      // A real control keeps its press: a checkbox, a sort header, a link.
      if ((e.target as HTMLElement).closest?.('input, button, a, select, textarea')) return;

      // Clear any flag a drag that never produced a click left behind.
      suppress = false;
      active = true;
      moved = false;
      startX = e.clientX;
      startLeft = el.scrollLeft;
    };

    const move = (e: PointerEvent) => {
      if (!active) return;
      const dx = e.clientX - startX;
      if (!moved) {
        if (Math.abs(dx) < DRAG_THRESHOLD) return;
        moved = true;
        setPanning(true);
        /* Captured only once it is a drag, so a plain click keeps its original
           target and still reaches the row underneath. */
        el.setPointerCapture?.(e.pointerId);
      }
      el.scrollLeft = startLeft - dx;
      e.preventDefault();
    };

    const up = (e: PointerEvent) => {
      if (!active) return;
      active = false;
      setPanning(false);
      if (el.hasPointerCapture?.(e.pointerId)) el.releasePointerCapture(e.pointerId);
      suppress = moved;
    };

    /* Cleared by the click it swallows, or by the next press. A flag rather
       than a timer, because "how long after a drag does a click arrive" has no
       answer that is right on every machine. */
    const click = (e: MouseEvent) => {
      if (!suppress) return;
      suppress = false;
      e.stopPropagation();
      e.preventDefault();
    };

    el.addEventListener('pointerdown', down);
    el.addEventListener('pointermove', move);
    el.addEventListener('pointerup', up);
    el.addEventListener('pointercancel', up);
    el.addEventListener('click', click, true);
    return () => {
      el.removeEventListener('pointerdown', down);
      el.removeEventListener('pointermove', move);
      el.removeEventListener('pointerup', up);
      el.removeEventListener('pointercancel', up);
      el.removeEventListener('click', click, true);
    };
  }, []);

  return { ref, pannable, panning };
}
